/**
 * County lookup index.
 *
 * Someone searching for their recording rules types a city ("Las Vegas"), a
 * county ("Clark"), a state, or an abbreviation. This builds a small scored
 * index over the county guides we publish so any of those resolve to the right
 * county page. Everything is derived from existing datasets — no new source.
 */
import {
  COUNTY_EASEMENT_GUIDES,
  countyGuidePath,
  type CountyEasementGuide,
} from "@/data/countyEasementGuides";
import { countyRecordingRuleFor } from "@/data/countyRecordingRules";
import { PRESCRIPTIVE_EASEMENT_LAW_SORTED } from "@/data/prescriptiveEasementLaw";
import { stateSlug } from "@/lib/state-law-routes";

export interface CountyLookupEntry {
  guide: CountyEasementGuide;
  /** "/easement-guides/nevada/clark" */
  path: string;
  /** "/easement-law/nevada" */
  stateLawPath: string;
  /** Office that takes the document. */
  office: string;
  /** True when we have transcribed this office's own filing rules. */
  verifiedRules: boolean;
  /** Prescriptive period for the state, when the dataset records one. */
  years: number | null;
  /** Lowercase strings this entry should match on. */
  haystack: string[];
}

const YEARS_BY_STATE = new Map(
  PRESCRIPTIVE_EASEMENT_LAW_SORTED.map((law) => [law.stateCode, law.years]),
);

function buildEntry(guide: CountyEasementGuide): CountyLookupEntry {
  const rule = countyRecordingRuleFor(guide.stateCode, guide.slug);
  const bareCounty = guide.county.replace(/\s+(County|Parish|Borough|City)$/i, "");
  return {
    guide,
    path: countyGuidePath(guide),
    stateLawPath: `/easement-law/${stateSlug(guide.stateName)}`,
    office: rule?.office ?? guide.recordingOffice,
    verifiedRules: Boolean(rule),
    years: YEARS_BY_STATE.get(guide.stateCode) ?? null,
    haystack: [
      guide.county,
      bareCounty,
      guide.metro,
      guide.stateName,
      guide.stateCode,
      guide.slug,
      guide.recordingOffice,
    ].map((value) => value.toLowerCase()),
  };
}

export const COUNTY_LOOKUP_ENTRIES: CountyLookupEntry[] = COUNTY_EASEMENT_GUIDES.map(
  buildEntry,
).sort(
  (a, b) =>
    a.guide.stateName.localeCompare(b.guide.stateName) ||
    a.guide.county.localeCompare(b.guide.county),
);

/** Counties grouped by state, for the browse list under the search box. */
export function countyLookupByState(): { stateName: string; entries: CountyLookupEntry[] }[] {
  const groups = new Map<string, CountyLookupEntry[]>();
  for (const entry of COUNTY_LOOKUP_ENTRIES) {
    const list = groups.get(entry.guide.stateName) ?? [];
    list.push(entry);
    groups.set(entry.guide.stateName, list);
  }
  return Array.from(groups.entries())
    .map(([stateName, entries]) => ({ stateName, entries }))
    .sort((a, b) => a.stateName.localeCompare(b.stateName));
}

function scoreEntry(entry: CountyLookupEntry, needle: string): number {
  let best = 0;
  for (const [index, field] of entry.haystack.entries()) {
    // Earlier fields (county, metro) outrank later ones (office name).
    const weight = Math.max(1, 8 - index);
    if (field === needle) best = Math.max(best, 100 + weight);
    else if (field.startsWith(needle)) best = Math.max(best, 60 + weight);
    else if (field.includes(needle)) best = Math.max(best, 30 + weight);
  }
  return best;
}

/**
 * Search counties by city, county name, state or recording office.
 * Returns the whole list, alphabetical, when the query is empty.
 */
export function searchCounties(query: string, limit = 60): CountyLookupEntry[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return COUNTY_LOOKUP_ENTRIES.slice(0, limit);
  return COUNTY_LOOKUP_ENTRIES.map((entry) => ({ entry, score: scoreEntry(entry, needle) }))
    .filter((row) => row.score > 0)
    .sort(
      (a, b) =>
        b.score - a.score ||
        a.entry.guide.county.localeCompare(b.entry.guide.county),
    )
    .slice(0, limit)
    .map((row) => row.entry);
}

/** Coverage counts used in the page copy and the tests. */
export function countyLookupStats() {
  const codes = new Set(COUNTY_LOOKUP_ENTRIES.map((e) => e.guide.stateCode));
  const includesDc = codes.delete("DC");
  return {
    counties: COUNTY_LOOKUP_ENTRIES.length,
    states: codes.size,
    includesDc,
    verified: COUNTY_LOOKUP_ENTRIES.filter((e) => e.verifiedRules).length,
  };
}
